import { Order } from "../queries/auto-generated/schemas";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../ducks";
import { useEffect, useState } from "react";
import { useApolloClient, ApolloQueryResult } from "@apollo/client";
import { LOOKUP_ORDER } from "../queries/my-queries";
import user from "../ducks/user";
import { LookupOrderQuery } from "../queries/auto-generated/operations";
import { BuiltItem } from "../types";
import { lineItemsToBuiltItems } from "../helpers";

const useOrderLookup = (jooApiOrderId: string) => {
  const {
    data: { variations, modifiers, items },
    loading: catalogLoading,
  } = useSelector((state: RootState) => state.catalog);

  const [orderLookupLoading, setOrderLookupLoading] = useState(false);

  const { refresher } = useOrderRefresh();

  let foundOrder: Order | null = null;
  let builtItems: BuiltItem[] = [];
  let missingMods = 0;
  let missingItems = 0;

  // Check for order in local store
  const localOrders = useSelector((state: RootState) => state.user.orders);
  if (localOrders[jooApiOrderId]) {
    foundOrder = localOrders[jooApiOrderId];

    // Get BuiltItems
    if (!catalogLoading) {
      const orderLineItems = foundOrder?.line_items;
      const {
        builtItems: convertedBuiltItems,
        missingMods: foundMissingMods,
        missingItems: foundMissingItems,
      } = lineItemsToBuiltItems(orderLineItems, variations, items, modifiers);
      builtItems = convertedBuiltItems;
      missingMods = foundMissingMods;
      missingItems = foundMissingItems;
    }
  }

  // Check for order from Joo API
  useEffect(() => {
    if (!foundOrder) {
      setOrderLookupLoading(true);
      refresher(jooApiOrderId, () => setOrderLookupLoading(false));
    }
    // this only needs to load on mount
    // eslint-disable-next-line
  }, []);

  return {
    order: foundOrder,
    loading: orderLookupLoading || catalogLoading,
    builtItems,
    missingMods,
    missingItems,
  };
};

export const useOrderRefresh = () => {
  const gqlClient = useApolloClient();
  const dispatch = useDispatch();

  const refresher = (
    jooApiOrderId?: string,
    finallyFunc?: (arg?: any) => void
  ) => {
    if (!jooApiOrderId) {
      return;
    }

    gqlClient
      .query({
        query: LOOKUP_ORDER,
        variables: {
          id: jooApiOrderId,
        },
        fetchPolicy: "network-only",
      })
      .then((result: ApolloQueryResult<LookupOrderQuery>) => {
        const { data } = result;
        if (!data?.order) {
          throw new Error("Order not found");
        }
        dispatch(user.actions.addOrder(data?.order as Order));
      })
      .catch((error) => console.error({ error }))
      .finally(finallyFunc);
  };

  return { refresher };
};

export default useOrderLookup;
